import Api from "@/main/api";
import Roles from "@/shared/Roles";
import AuthService from "@/shared/AuthService";

/**
 * Single decision point for the mandatory passkey + TOTP enrollment gate.
 *
 * Passkey presence has no flag on the user object, so we fetch it once via
 * Api.passkey.listCredentials() and cache it for the session. TOTP status comes
 * from user.mfa (already in the store). Frontend gate only — backend should also
 * enforce. (C) 2026
 */

let passkeyKnown = false;   // have we successfully fetched?
let passkeyPresent = false; // cached result
let inflight = null;

// Accept both shapes: officer store -> { user: {...} }; admin store -> {...}.
function userObj(user) {
  return (user && user.user ? user.user : user) || {};
}

async function hasPasskey(force = false) {
  if (passkeyKnown && !force) return passkeyPresent;
  if (inflight) return inflight;
  inflight = Api.passkey
    .listCredentials()
    .then((res) => {
      passkeyPresent = Array.isArray(res.data) ? res.data.length > 0 : false;
      passkeyKnown = true;
      return passkeyPresent;
    })
    .catch(() => {
      // Fail safe: treat as no passkey, do NOT cache so we retry next time.
      passkeyKnown = false;
      passkeyPresent = false;
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function setHasPasskey(present) {
  passkeyPresent = !!present;
  passkeyKnown = true;
}

function clearCache() {
  passkeyKnown = false;
  passkeyPresent = false;
  inflight = null;
}

function requiresPasskey(user) {
  return Roles.deepContains(AuthService.requirePasskey, userObj(user).roles ?? []);
}

async function needsPasskeyEnrollment(user) {
  return requiresPasskey(user) && !(await hasPasskey());
}

// Officer-app check: blocked if TOTP-enforced without mfa, or passkey-enforced without passkey.
async function isBlocked(user) {
  const u = userObj(user);
  const needsMfa = Roles.deepContains(AuthService.require2fa, u.roles ?? []) && !u.mfa;
  return needsMfa || (await needsPasskeyEnrollment(user));
}

export default {
  hasPasskey,
  setHasPasskey,
  clearCache,
  requiresPasskey,
  needsPasskeyEnrollment,
  isBlocked,
};
